import {
  DEFAULT_CATALOG,
  FREE_SKU,
  LIMIT_HISTORY_VIEW_DAYS,
  LIMIT_LEVELING_RULES,
  LIMIT_PERKS,
  LIMIT_PERK_CONDITIONS,
  LIMIT_ANNOTATION_RULES,
  entitledLimit,
  levelingRuleCeiling,
  perkCeiling,
  perkConditionCeiling,
  annotationRuleCeiling,
  rankOf,
  resolveEntitlement,
  historyViewDays,
} from "@colossus/contracts";
import type { Entitlement, GrantValue } from "@colossus/contracts";
import { TEAM_BILLING_WRITE, has } from "./access.ts";
import { getLocale } from "./locale.ts";
import { m } from "./paraglide/messages.js";
import { skuName } from "./plan.ts";
import { priceLabel } from "./pricing.ts";
import type { ProductPrice } from "./pricing.ts";
import { shiftDays } from "./scope.ts";
import { billingHref } from "./settings.ts";
import type { LockedScene } from "./ui/LockedPreview.svelte";
import type { UpgradePitch } from "./ui/upgrade.svelte.ts";

export type Gate = { readonly grant: string } | { readonly limit: string; readonly reach: number };

const ceilingOf = (entitlement: Entitlement, key: string): number | undefined => {
  if (key === LIMIT_HISTORY_VIEW_DAYS) return historyViewDays(entitlement);
  if (key === LIMIT_LEVELING_RULES) return levelingRuleCeiling(entitlement) ?? undefined;
  if (key === LIMIT_PERKS) return perkCeiling(entitlement) ?? undefined;
  if (key === LIMIT_PERK_CONDITIONS) return perkConditionCeiling(entitlement);
  if (key === LIMIT_ANNOTATION_RULES) return annotationRuleCeiling(entitlement) ?? undefined;
  return entitledLimit(entitlement, key);
};

const clears = (entitlement: Entitlement, gate: Gate): boolean => {
  if ("grant" in gate) return entitlement.grants.includes(gate.grant);
  const ceiling = ceilingOf(entitlement, gate.limit);
  return ceiling === undefined || ceiling >= gate.reach;
};

export const gated = (entitlement: Entitlement, gate: Gate): boolean => !clears(entitlement, gate);

interface CatalogEntry {
  readonly sku: string;
  readonly kind: string;
  readonly grants: readonly { readonly key: string; readonly value: GrantValue }[];
  readonly price?: ProductPrice | null;
}

export type Ladder = readonly {
  readonly sku: string;
  readonly entitlement: Entitlement;
  readonly price: ProductPrice | null;
}[];

export const ladderFrom = (catalog: readonly CatalogEntry[]): Ladder => {
  const grants = catalog.flatMap((entry) =>
    entry.grants.map((grant) => ({ sku: entry.sku, grantKey: grant.key, value: grant.value })),
  );
  return catalog
    .filter((entry) => entry.kind === "tier")
    .toSorted((left, right) => rankOf(left.sku) - rankOf(right.sku))
    .map((entry) => ({
      sku: entry.sku,
      entitlement: resolveEntitlement(grants, [entry.sku]),
      price: entry.price ?? null,
    }));
};

export const SEED_LADDER: Ladder = ladderFrom(DEFAULT_CATALOG);

export const liftingTier = (ladder: Ladder, gate: Gate): string | null =>
  ladder.find((tier) => clears(tier.entitlement, gate))?.sku ?? null;

const covers = (held: Entitlement, tier: Entitlement): boolean =>
  tier.grants.every((grant) => clears(held, { grant })) &&
  Object.entries(tier.limits).every(([limit, reach]) => clears(held, { limit, reach }));

export const isFree = (ladder: Ladder, entitlement: Entitlement): boolean =>
  rankOf(ladder.findLast((tier) => covers(entitlement, tier.entitlement))?.sku ?? FREE_SKU) ===
  rankOf(FREE_SKU);

export const offeredOnce = (tiers: readonly (string | null)[]): readonly boolean[] => {
  const offered = new Set<number>();
  return tiers.map((tier) => {
    if (tier === null) return false;
    const rank = rankOf(tier);
    if (offered.has(rank)) return false;
    offered.add(rank);
    return true;
  });
};

export const nearLimit = (used: number, max: number): boolean =>
  max - used <= Math.max(1, Math.floor(max / 5));

export const PLAN_PARAM = "plan";

export const upgradeHref = (
  tenantId: string,
  permissions: readonly string[],
  tier: string | null,
): string | null => {
  if (!has(permissions, TEAM_BILLING_WRITE)) return null;
  return billingHref(
    tenantId,
    "billing",
    tier === null ? null : new URLSearchParams({ [PLAN_PARAM]: tier }),
  );
};

export const tierPrice = (ladder: Ladder, tier: string): string | null => {
  const rank = rankOf(tier);
  const prices = ladder.flatMap((entry) =>
    rankOf(entry.sku) === rank && entry.price !== null ? [entry.price] : [],
  );
  const [cheapest] = prices.toSorted((left, right) => left.amountCents - right.amountCents);
  if (cheapest === undefined) return null;
  const label = priceLabel(getLocale(), cheapest);
  return prices.some((price) => price.amountCents !== cheapest.amountCents)
    ? m.pricing_from({ price: label })
    : label;
};

export const historyViewFloor = (entitlement: Entitlement, today: string): string | null => {
  const ceiling = historyViewDays(entitlement);
  return ceiling === undefined ? null : shiftDays(today, -ceiling);
};

export const historyGate = (since: string, today: string): Gate => ({
  limit: LIMIT_HISTORY_VIEW_DAYS,
  reach: Math.round((Date.parse(today) - Date.parse(since)) / 86_400_000),
});

export interface HistoryLimit {
  readonly today: string;
  readonly floor: string;
  readonly tier: string;
  readonly href: string | null;
  readonly ladder: Ladder;
}

export const historyLimit = (
  ladder: Ladder,
  entitlement: Entitlement,
  today: string,
  reader: { readonly tenantId: string; readonly permissions: readonly string[] },
): HistoryLimit | null => {
  const floor = historyViewFloor(entitlement, today);
  if (floor === null) return null;
  const tier = liftingTier(ladder, historyGate(shiftDays(floor, -1), today));
  if (tier === null) return null;
  return {
    today,
    floor,
    tier,
    href: upgradeHref(reader.tenantId, reader.permissions, tier),
    ladder,
  };
};

export const historyPitch = (history: {
  readonly tier: string;
  readonly href: string | null;
  readonly ladder: Ladder;
}): UpgradePitch => ({
  title: m.upsell_history_title({ tier: skuName(history.tier) }),
  message: m.upsell_history_body(),
  tier: history.tier,
  href: history.href,
  ladder: history.ladder,
  scene: "history",
  testId: "history-upgrade-dialog",
});

export const rangeOutside = (
  since: string,
  today: string,
  floor: string | null,
): "future" | "floor" | null => {
  const start = since.slice(0, 10);
  if (start > today) return "future";
  if (floor !== null && start < floor) return "floor";
  return null;
};

export const surfacePitch = (
  reader: {
    readonly tenantId: string;
    readonly permissions: readonly string[];
    readonly ladder: Ladder;
  },
  lock: {
    readonly tier: string | null;
    readonly title: string;
    readonly message: string;
    readonly scene: LockedScene;
    readonly testId: string;
  },
): UpgradePitch => ({
  title: lock.title,
  message: lock.message,
  tier: lock.tier,
  href: upgradeHref(reader.tenantId, reader.permissions, lock.tier),
  ladder: reader.ladder,
  scene: lock.scene,
  testId: `${lock.testId}-dialog`,
});
